'use client';

import { toast } from '@repo/design-system/components/ui/toaster';
import { useHostInfo } from '@repo/host/hooks/use-host-info';
import { getHost, supportsHostAuthToken } from '@repo/host/host';
import { useT } from '@repo/i18n/components/i18n.client';
import type { PreferenceFieldKey, PreferencesClientMessage, PreferencesRecord, TrackedCompassPin } from '@repo/map-preferences/types';
import { isPreferencesServerMessage, parseTrackedCompassPin, toMapPreferencesAuthProtocol } from '@repo/map-preferences/types';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectAuthToken, selectIsAuthenticated } from '@repo/store/maps/slices/auth';
import { selectActiveCategoryId, selectActiveDevelopmentMessageDismissedAt, selectShowTeleportWarning, selectSidebarOpen, setActiveCategoryId, setActiveDevelopmentMessageDismissedAt, setShowTeleportWarning, setSidebarOpen } from '@repo/store/maps/slices/clientContext';
import type { FilterState, LayerVisibility } from '@repo/store/maps/slices/mapData';
import { addFogCircles, clearFog, clearSelectedMarker, hydrateFogCircles, selectExternalMapId, selectFilterState, selectLayerVisibility, selectMapData, selectMarker, selectSelectedMarkerId, selectTrackedCompassPin, setFilterStateFromUrl, setLayerVisibility, setLocationEnabled, setPlayerBearing, setPlayerCoords, setTrackedCompassPin, setTrainerConnected } from '@repo/store/maps/slices/mapData';
import { HostType } from '@repo/types/host';
import { extractUserUuid } from '@repo/utility/jwt';
import { createLogger } from '@repo/utility/logging';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fogSpatialIndex } from '../../fog-of-war/fog-spatial-index';
import { PlayerPositionLease } from './player-position-lease';
export interface UseMapPreferencesSyncReturn {
  isReady: boolean;
  isConnected: boolean;
  hasStoredPreferences: boolean;
  /** Ref for the consuming component to provide a handler for remote marker selections (flyTo + visual state). */
  onRemoteMarkerSelectRef: React.MutableRefObject<((markerId: string | null) => void) | null>;
  /** Ref to the WebSocket sendMessage function for sending ephemeral messages (e.g., player position). */
  sendMessageRef: React.MutableRefObject<((message: PreferencesClientMessage) => void) | null>;
  requestMiniMapOpen: () => Promise<boolean>;
}
interface UseMapPreferencesSyncOptions {
  syncSelectedMarker?: boolean;
}
const RECONNECT_DELAYS = [1000, 2000, 5000, 10000, 30000];
const MAX_RECONNECT_ATTEMPTS = 10;
const DEBOUNCE_MS = 300;
const SYNC_TIMEOUT_MS = 3000;
const MAX_PENDING_FOG_CIRCLES = 5_000;
const FOG_FLUSH_BATCH_SIZE = 500;
const MINI_MAP_OPEN_TIMEOUT_MS = 5_000;
const logger = createLogger({
  environment: process.env.NEXT_PUBLIC_WRANGLER_ENV ?? process.env.NODE_ENV ?? 'production',
  color: 'orange',
  prefix: 'MapPrefs'
});
interface FieldConfig {
  serialize: (state: ReduxPreferenceState) => string;
  apply: (dispatch: ReturnType<typeof useAppDispatch>, value: string) => void;
}
interface ReduxPreferenceState {
  selectedMarkerId: string | null;
  activeCategoryId: string | null;
  sidebarOpen: boolean;
  hiddenSubcategories: string[];
  hiddenRegions: string[];
  regionFilterMode: FilterState['regionFilterMode'];
  hideAll: boolean;
  teleportOnly: boolean;
  unfoundOnly: boolean;
  layerVisibility: LayerVisibility;
  showTeleportWarning: boolean;
  trackedCompassPin: TrackedCompassPin | null;
  activeDevelopmentMessageDismissedAt: string | null;
}
const STATIC_FIELD_CONFIG: Partial<Record<PreferenceFieldKey, FieldConfig>> = {
  selectedMarkerId: {
    serialize: state => state.selectedMarkerId ?? '',
    apply: (dispatch, value) => {
      if (value) {
        dispatch(selectMarker(value));
      } else {
        dispatch(clearSelectedMarker());
      }
    }
  },
  activeCategoryId: {
    serialize: state => state.activeCategoryId ?? '',
    apply: (dispatch, value) => dispatch(setActiveCategoryId(value || null))
  },
  hiddenSubcategories: {
    serialize: state => JSON.stringify(state.hiddenSubcategories),
    apply: (dispatch, value) => {
      const parsed: unknown = JSON.parse(value);
      if (Array.isArray(parsed)) {
        dispatch(setFilterStateFromUrl({
          hiddenSubcategories: parsed
        }));
      }
    }
  },
  hiddenRegions: {
    serialize: state => JSON.stringify(state.hiddenRegions),
    apply: (dispatch, value) => {
      const parsed: unknown = JSON.parse(value);
      if (Array.isArray(parsed)) {
        dispatch(setFilterStateFromUrl({
          hiddenRegions: parsed
        }));
      }
    }
  },
  regionFilterMode: {
    serialize: state => state.regionFilterMode,
    apply: (dispatch, value) => {
      if (value === 'exclude' || value === 'include') {
        dispatch(setFilterStateFromUrl({
          regionFilterMode: value
        }));
      }
    }
  },
  hideAll: {
    serialize: state => String(state.hideAll),
    apply: (dispatch, value) => dispatch(setFilterStateFromUrl({
      hideAll: value === 'true'
    }))
  },
  teleportOnly: {
    serialize: state => String(state.teleportOnly),
    apply: (dispatch, value) => dispatch(setFilterStateFromUrl({
      teleportOnly: value === 'true'
    }))
  },
  unfoundOnly: {
    serialize: state => String(state.unfoundOnly),
    apply: (dispatch, value) => dispatch(setFilterStateFromUrl({
      unfoundOnly: value === 'true'
    }))
  },
  layerVisibility: {
    serialize: state => JSON.stringify(state.layerVisibility),
    apply: (dispatch, value) => {
      const parsed: unknown = JSON.parse(value);
      if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
        dispatch(setLayerVisibility(parsed as LayerVisibility));
      }
    }
  },
  showTeleportWarning: {
    serialize: state => String(state.showTeleportWarning),
    apply: (dispatch, value) => dispatch(setShowTeleportWarning(value === 'true'))
  },
  trackedCompassPin: {
    serialize: state => state.trackedCompassPin ? JSON.stringify(state.trackedCompassPin) : '',
    apply: (dispatch, value) => dispatch(setTrackedCompassPin(parseTrackedCompassPin(value)))
  },
  activeDevelopmentMessageDismissedAt: {
    serialize: state => state.activeDevelopmentMessageDismissedAt ?? '',
    apply: (dispatch, value) => dispatch(setActiveDevelopmentMessageDismissedAt(value || null))
  }
};

/** Fields that represent user-set filter preferences (used to detect conflict with URL filter params). */
const FILTER_FIELDS = new Set<string>(['activeCategoryId', 'hiddenSubcategories', 'hiddenRegions', 'regionFilterMode', 'hideAll', 'teleportOnly', 'unfoundOnly', 'layerVisibility']);
const SIDEBAR_FIELD_CONFIG: FieldConfig = {
  serialize: state => String(state.sidebarOpen),
  apply: (dispatch, value) => dispatch(setSidebarOpen(value === 'true'))
};
function collectReduxState(selectedMarkerId: string | null, activeCategoryId: string | null, sidebarOpen: boolean, filters: {
  hiddenSubcategories: string[];
  hiddenRegions: string[];
  regionFilterMode: FilterState['regionFilterMode'];
  hideAll: boolean;
  teleportOnly: boolean;
  unfoundOnly: boolean;
}, layerVisibility: LayerVisibility, showTeleportWarning: boolean, trackedCompassPin: TrackedCompassPin | null, activeDevelopmentMessageDismissedAt: string | null): ReduxPreferenceState {
  return {
    selectedMarkerId,
    activeCategoryId,
    sidebarOpen,
    hiddenSubcategories: filters.hiddenSubcategories,
    hiddenRegions: filters.hiddenRegions,
    regionFilterMode: filters.regionFilterMode,
    hideAll: filters.hideAll,
    teleportOnly: filters.teleportOnly,
    unfoundOnly: filters.unfoundOnly,
    layerVisibility,
    showTeleportWarning,
    trackedCompassPin,
    activeDevelopmentMessageDismissedAt
  };
}
export function useMapPreferencesSync(options?: UseMapPreferencesSyncOptions): UseMapPreferencesSyncReturn {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const token = useAppSelector(selectAuthToken);
  const mapExternalId = useAppSelector(selectExternalMapId);
  const mapData = useAppSelector(selectMapData);
  const sidebarOpen = useAppSelector(selectSidebarOpen);
  const filters = useAppSelector(selectFilterState);
  const layerVisibility = useAppSelector(selectLayerVisibility);
  const selectedMarkerId = useAppSelector(selectSelectedMarkerId);
  const activeCategoryId = useAppSelector(selectActiveCategoryId);
  const showTeleportWarning = useAppSelector(selectShowTeleportWarning);
  const trackedCompassPin = useAppSelector(selectTrackedCompassPin);
  const activeDevelopmentMessageDismissedAt = useAppSelector(selectActiveDevelopmentMessageDismissedAt);
  const {
    hostType,
    hostMode,
    rpcType
  } = useHostInfo();
  const canUseHostAuth = supportsHostAuthToken({
    type: hostType,
    mode: hostMode,
    rpc: rpcType
  });
  const {
    t
  } = useT(['game_map']);
  const syncSelectedMarker = options?.syncSelectedMarker ?? true;
  const sidebarFieldKey = `sidebarOpen:${hostType}` as PreferenceFieldKey;
  const fieldConfig = useMemo(() => {
    const config: Partial<Record<PreferenceFieldKey, FieldConfig>> = {
      ...STATIC_FIELD_CONFIG,
      [sidebarFieldKey]: SIDEBAR_FIELD_CONFIG
    };
    if (!syncSelectedMarker) {
      delete config.selectedMarkerId;
    }
    return config as Record<PreferenceFieldKey, FieldConfig>;
  }, [sidebarFieldKey, syncSelectedMarker]);
  const fieldKeys = useMemo(() => Object.keys(fieldConfig) as PreferenceFieldKey[], [fieldConfig]);
  const [syncReceived, setSyncReceived] = useState(false);
  const [syncCount, setSyncCount] = useState(0);
  const [prefsSettled, setPrefsSettled] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [hasStoredPreferences, setHasStoredPreferences] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRemoteUpdateRef = useRef(false);
  const overlayDefaultAppliedRef = useRef(false);
  const localTimestampsRef = useRef<Record<string, number>>({});
  const lastSentValuesRef = useRef<Record<string, string>>({});
  const pendingChangesRef = useRef<PreferencesRecord>({});
  const activeSessionKeyRef = useRef<string | null>(null);
  const initialSyncReceivedRef = useRef(false);
  const lastSyncedFieldsRef = useRef<PreferencesRecord>({});
  const baselineSeededRef = useRef(false);
  const lastFogLimitToastRef = useRef(0);
  const pendingFogClearRef = useRef(false);
  const pendingFogCirclesRef = useRef<Array<[number, number]>>([]);
  const trainerPresenceRef = useRef<Extract<PreferencesClientMessage, {
    type: 'trainer_presence';
  }> | null>(null);
  const pendingMiniMapOpenRequestsRef = useRef(new Map<string, {
    resolve: (success: boolean) => void;
    timeout: ReturnType<typeof setTimeout>;
  }>());
  const activeDevelopmentMessageDismissedAtRef = useRef(activeDevelopmentMessageDismissedAt);
  // Consumed by the next hydrate so the DO's stale auto-push on accept doesn't clobber our just-flushed writes.
  const skipNextHydrateRef = useRef(false);
  const onRemoteMarkerSelectRef = useRef<((markerId: string | null) => void) | null>(null);
  const sendMessageRef = useRef<((message: PreferencesClientMessage) => void) | null>(null);
  const mapDataRef = useRef(mapData);
  mapDataRef.current = mapData;
  activeDevelopmentMessageDismissedAtRef.current = activeDevelopmentMessageDismissedAt;
  const sendMessage = useCallback((message: PreferencesClientMessage) => {
    if (message.type === 'trainer_presence') {
      trainerPresenceRef.current = message;
    }
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return;
    }
    if (message.type === 'fog_clear') {
      pendingFogClearRef.current = true;
      pendingFogCirclesRef.current = [];
    } else if (message.type === 'fog_circles_add') {
      const room = MAX_PENDING_FOG_CIRCLES - pendingFogCirclesRef.current.length;
      if (room > 0) {
        const toQueue = message.circles.slice(0, room);
        pendingFogCirclesRef.current.push(...toQueue);
      }
    }
  }, []);
  const resolvePendingMiniMapOpenRequests = useCallback((success: boolean) => {
    for (const pending of pendingMiniMapOpenRequestsRef.current.values()) {
      clearTimeout(pending.timeout);
      pending.resolve(success);
    }
    pendingMiniMapOpenRequestsRef.current.clear();
  }, []);
  const requestMiniMapOpen = useCallback((): Promise<boolean> => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) return Promise.resolve(false);
    const requestId = crypto.randomUUID();
    return new Promise(resolve => {
      const timeout = setTimeout(() => {
        pendingMiniMapOpenRequestsRef.current.delete(requestId);
        resolve(false);
      }, MINI_MAP_OPEN_TIMEOUT_MS);
      pendingMiniMapOpenRequestsRef.current.set(requestId, {
        resolve,
        timeout
      });
      sendMessage({
        type: 'mini_map_open_request',
        requestId
      });
    });
  }, [sendMessage]);
  const handleMiniMapOpenRequest = useCallback(async (requestId: string): Promise<void> => {
    if (hostType !== HostType.APP && hostType !== HostType.OVERLAY) return;
    const success = await getHost().openMiniMap().catch(() => false);
    sendMessage({
      type: 'mini_map_open_result',
      requestId,
      success
    });
  }, [hostType, sendMessage]);

  // On overlay, default the sidebar to closed when no stored preference will be applied.
  // Runs at most once per mount — subsequent user toggles sync normally to the DO.
  const applyOverlayDefaultIfNeeded = useCallback((storedSidebarValue: string | undefined) => {
    if (hostType !== HostType.OVERLAY) return;
    if (overlayDefaultAppliedRef.current) return;
    if (storedSidebarValue !== undefined) return;
    overlayDefaultAppliedRef.current = true;
    dispatch(setSidebarOpen(false));
  }, [dispatch, hostType]);
  useEffect(() => {
    sendMessageRef.current = sendMessage;
    return () => {
      sendMessageRef.current = null;
    };
  }, [sendMessage]);
  const applyRemoteFields = useCallback((fields: PreferencesRecord) => {
    isRemoteUpdateRef.current = true;
    try {
      for (const key of fieldKeys) {
        const field = fields[key];
        if (!field) continue;
        const config = fieldConfig[key];
        if (!config) continue;
        if (key === 'selectedMarkerId' && !mapDataRef.current) continue;
        const localTs = localTimestampsRef.current[key] ?? 0;
        if (field.updatedAt >= localTs) {
          logger.info(`Apply remote field: ${key} = ${field.value}`);
          try {
            config.apply(dispatch, field.value);
            if (key === 'selectedMarkerId') {
              onRemoteMarkerSelectRef.current?.(field.value || null);
            }
          } catch (err) {
            logger.warn(`Failed to apply field: ${key}`, {
              error: err instanceof Error ? err.message : String(err)
            });
            continue;
          }
          localTimestampsRef.current[key] = field.updatedAt;
          lastSentValuesRef.current[key] = field.value;
        } else {
          logger.info(`Skip stale remote field: ${key} (remote=${field.updatedAt}, local=${localTs})`);
        }
      }
    } finally {
      isRemoteUpdateRef.current = false;
    }
  }, [dispatch, fieldConfig, fieldKeys]);
  const clearPlayerLocation = useCallback(() => {
    dispatch(setLocationEnabled(false));
    dispatch(setPlayerCoords(null));
    dispatch(setPlayerBearing(null));
  }, [dispatch]);
  const playerPositionLeaseRef = useRef<PlayerPositionLease | null>(null);
  playerPositionLeaseRef.current ??= new PlayerPositionLease(clearPlayerLocation);
  const clearTrainerState = useCallback(() => {
    dispatch(setTrainerConnected(false));
    playerPositionLeaseRef.current?.expire();
  }, [dispatch]);
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const data: unknown = JSON.parse(event.data as string);
      if (!isPreferencesServerMessage(data)) return;
      switch (data.type) {
        case 'preferences_sync':
          {
            if (syncTimeoutRef.current) {
              clearTimeout(syncTimeoutRef.current);
              syncTimeoutRef.current = null;
            }
            const fieldKeys = Object.keys(data.fields);
            logger.info(`Sync received: ${fieldKeys.length} fields${fieldKeys.length > 0 ? ` [${fieldKeys.join(', ')}]` : ' (empty — using defaults)'}`);
            setHasStoredPreferences(Object.keys(data.fields).some(k => FILTER_FIELDS.has(k)));
            lastSyncedFieldsRef.current = data.fields;
            applyRemoteFields(data.fields);
            if (!data.fields.activeDevelopmentMessageDismissedAt && activeDevelopmentMessageDismissedAtRef.current) {
              dispatch(setActiveDevelopmentMessageDismissedAt(null));
            }
            applyOverlayDefaultIfNeeded(data.fields[sidebarFieldKey]?.value);
            initialSyncReceivedRef.current = true;
            setSyncReceived(true);
            setSyncCount(c => c + 1);
            break;
          }
        case 'preferences_updated':
          {
            logger.info(`Remote update received: ${Object.keys(data.fields).join(', ')}`);
            applyRemoteFields(data.fields);
            break;
          }
        case 'error':
          {
            logger.error(`Server error: ${data.message}`);
            if (data.message === 'fog_circles_limit_exceeded') {
              const now = Date.now();
              if (now - lastFogLimitToastRef.current > 60_000) {
                lastFogLimitToastRef.current = now;
                toast({
                  description: t($ => $.fog_circles_limit_reached),
                  variant: 'error'
                });
              }
            }
            break;
          }
        case 'trainer_presence':
          {
            if (hostType === HostType.APP || hostType === HostType.OVERLAY) break;
            if (data.connected) {
              dispatch(setTrainerConnected(true));
            } else {
              clearTrainerState();
            }
            break;
          }
        case 'player_position':
          {
            dispatch(setPlayerCoords(data.coords));
            dispatch(setPlayerBearing(data.bearing));
            dispatch(setTrainerConnected(true));
            dispatch(setLocationEnabled(true));
            playerPositionLeaseRef.current?.refresh();
            break;
          }
        case 'player_position_stop':
          {
            playerPositionLeaseRef.current?.expire();
            break;
          }
        case 'mini_map_open_result':
          {
            const pending = pendingMiniMapOpenRequestsRef.current.get(data.requestId);
            if (!pending) break;
            clearTimeout(pending.timeout);
            pendingMiniMapOpenRequestsRef.current.delete(data.requestId);
            pending.resolve(data.success);
            break;
          }
        case 'mini_map_open_request':
          void handleMiniMapOpenRequest(data.requestId);
          break;
        case 'fog_circles_hydrate':
          {
            if (skipNextHydrateRef.current) {
              skipNextHydrateRef.current = false;
              logger.info('Skipping stale hydrate (queued writes flushed; awaiting fresh state)');
              break;
            }
            if (Array.isArray(data.circles)) {
              fogSpatialIndex.rebuild(data.circles);
              dispatch(hydrateFogCircles(data.circles));
            }
            break;
          }
        case 'fog_circles_delta':
          {
            if (Array.isArray(data.circles) && data.circles.length > 0) {
              fogSpatialIndex.addCircles(data.circles);
              dispatch(addFogCircles(data.circles));
            }
            break;
          }
        case 'fog_cleared':
          {
            fogSpatialIndex.clear();
            dispatch(clearFog());
            break;
          }
      }
    } catch (err) {
      logger.error('Failed to parse map preferences message', {
        error: err instanceof Error ? err.message : String(err)
      });
    }
  }, [applyRemoteFields, applyOverlayDefaultIfNeeded, clearTrainerState, dispatch, handleMiniMapOpenRequest, hostType, sidebarFieldKey, t]);
  useEffect(() => {
    if (!mapExternalId) {
      // Map data hasn't loaded yet — don't settle prefs until we know
      // whether the user is authenticated.
      return;
    }
    if (!isAuthenticated || !token) {
      activeSessionKeyRef.current = null;
      if (hostType === HostType.WEB || hostType === HostType.REMOTE) clearTrainerState();
      logger.info('Skipping — not authenticated');
      applyOverlayDefaultIfNeeded(undefined);
      setSyncReceived(true);
      setPrefsSettled(true);
      return;
    }
    const userId = extractUserUuid(token);
    if (!userId) {
      activeSessionKeyRef.current = null;
      logger.info('Skipping — could not extract userId from token');
      applyOverlayDefaultIfNeeded(undefined);
      setSyncReceived(true);
      setPrefsSettled(true);
      return;
    }
    const sessionKey = `${userId}:${mapExternalId}`;
    if (sessionKey !== activeSessionKeyRef.current) {
      activeSessionKeyRef.current = sessionKey;
      localTimestampsRef.current = {};
      lastSentValuesRef.current = {};
      pendingChangesRef.current = {};
      lastSyncedFieldsRef.current = {};
    }
    let aborted = false;
    initialSyncReceivedRef.current = false;
    baselineSeededRef.current = false;
    overlayDefaultAppliedRef.current = false;
    pendingFogClearRef.current = false;
    pendingFogCirclesRef.current = [];
    skipNextHydrateRef.current = false;
    setSyncReceived(false);
    setPrefsSettled(false);
    setHasStoredPreferences(false);

    // Start sync timeout immediately — if we don't receive sync within this
    // window (whether due to connection failure, slow server, or anything else),
    // unblock the map with defaults. This ensures pref sync never blocks usage.
    syncTimeoutRef.current = setTimeout(() => {
      if (!aborted && !initialSyncReceivedRef.current) {
        logger.warn('Sync timeout — falling back to defaults');
        applyOverlayDefaultIfNeeded(undefined);
        setSyncReceived(true);
        setPrefsSettled(true);
      }
    }, SYNC_TIMEOUT_MS);
    const connect = () => {
      if (aborted) return;
      const envWsUrl = process.env.NEXT_PUBLIC_MAP_PREFS_WS_URL;
      let resolved: string;
      if (envWsUrl) {
        resolved = `${envWsUrl}?sessionKey=${encodeURIComponent(sessionKey)}`;
      } else {
        const proto = typeof window !== 'undefined' && window.location?.protocol === 'https:' ? 'wss:' : 'ws:';
        const host = typeof window !== 'undefined' ? window.location.host : '';
        resolved = `${proto}//${host}/maps/api/map-preferences/ws?sessionKey=${encodeURIComponent(sessionKey)}`;
      }
      logger.info(`Connecting to ${resolved}`);
      const ws = new WebSocket(resolved, [toMapPreferencesAuthProtocol(token)]);
      wsRef.current = ws;
      ws.onopen = () => {
        if (aborted) return;
        logger.info('Connected');
        setIsConnected(true);
        reconnectAttemptRef.current = 0;
        if (trainerPresenceRef.current !== null) {
          sendMessage(trainerPresenceRef.current);
        }
        const queuedClear = pendingFogClearRef.current;
        const queuedCircles = pendingFogCirclesRef.current;
        const hasQueuedFogWrites = queuedClear || queuedCircles.length > 0;
        if (queuedClear) {
          pendingFogClearRef.current = false;
          logger.info('Flushing queued fog_clear after reconnect');
          sendMessage({
            type: 'fog_clear'
          });
        }
        if (queuedCircles.length > 0) {
          pendingFogCirclesRef.current = [];
          logger.info(`Flushing ${queuedCircles.length} queued fog circles after reconnect`);
          for (let i = 0; i < queuedCircles.length; i += FOG_FLUSH_BATCH_SIZE) {
            sendMessage({
              type: 'fog_circles_add',
              circles: queuedCircles.slice(i, i + FOG_FLUSH_BATCH_SIZE)
            });
          }
        }
        if (hasQueuedFogWrites) {
          skipNextHydrateRef.current = true;
          sendMessage({
            type: 'preferences_request'
          });
        }
        const pending = pendingChangesRef.current;
        if (Object.keys(pending).length > 0) {
          pendingChangesRef.current = {};
          logger.info(`Flushing ${Object.keys(pending).length} pending changes after reconnect`);
          sendMessage({
            type: 'preferences_update',
            fields: pending
          });
        }
      };
      ws.onmessage = event => {
        if (aborted) return;
        handleMessage(event);
      };
      ws.onclose = event => {
        if (aborted) return;
        logger.info(`Disconnected code=${event.code}${event.reason ? ` ${event.reason}` : ''}`);
        setIsConnected(false);
        wsRef.current = null;
        resolvePendingMiniMapOpenRequests(false);
        if (event.code !== 1000 && reconnectAttemptRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = RECONNECT_DELAYS[Math.min(reconnectAttemptRef.current, RECONNECT_DELAYS.length - 1)];
          logger.info(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptRef.current + 1})`);
          reconnectAttemptRef.current += 1;
          reconnectTimeoutRef.current = setTimeout(() => connect(), delay);
        } else {
          if (hostType === HostType.WEB || hostType === HostType.REMOTE) clearTrainerState();
          if (!initialSyncReceivedRef.current) {
            // All reconnect attempts exhausted without ever syncing — unblock the map.
            logger.warn('Preference sync unavailable — falling back to defaults');
            setSyncReceived(true);
            setPrefsSettled(true);
          }
        }
      };
      ws.onerror = () => {
        if (aborted) return;
        logger.error('WebSocket connection error');
      };
    };
    connect();
    return () => {
      aborted = true;
      // Without this, FogOfWarLayer keeps rendering the previous map's circles
      // until fog_circles_hydrate arrives; clearFog also bumps hydrationVersion → resetGrid.
      fogSpatialIndex.clear();
      dispatch(clearFog());
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      playerPositionLeaseRef.current?.dispose();
      const pendingChanges = pendingChangesRef.current;
      pendingChangesRef.current = {};
      if (wsRef.current?.readyState === WebSocket.OPEN && Object.keys(pendingChanges).length > 0) {
        wsRef.current.send(JSON.stringify({
          type: 'preferences_update',
          fields: pendingChanges
        }));
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.onopen = null;
        wsRef.current.onmessage = null;
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        wsRef.current.close(1000, 'Cleanup');
        wsRef.current = null;
      }
      resolvePendingMiniMapOpenRequests(false);
    };
  }, [isAuthenticated, token, mapExternalId, handleMessage, sendMessage, applyOverlayDefaultIfNeeded, clearTrainerState, dispatch, hostType, resolvePendingMiniMapOpenRequests]);

  // Re-apply synced preferences after setCompleteMapData overwrites filter defaults.
  // setCompleteMapData appends hidden-by-default subcategories to filters, which
  // clobbers the synced values. This re-applies them and marks ready-to-seed.
  const [needsBaselineSeed, setNeedsBaselineSeed] = useState(false);
  // syncCount is included so this effect re-runs when WS sync arrives
  // after mapData is already available (SSR flow). Unlike syncReceived (boolean),
  // syncCount always increments so the effect re-fires even when syncReceived
  // was already set to true by a sync timeout fallback.
  // biome-ignore lint/correctness/useExhaustiveDependencies: syncCount intentionally triggers re-run when sync arrives after mapData
  useEffect(() => {
    // Only run during initialization — once the baseline is seeded, subsequent
    // mapData changes (e.g. found pin toggles mutating feature properties) must
    // not re-apply all preferences, as that causes a full re-render cascade.
    if (!mapData || !initialSyncReceivedRef.current || baselineSeededRef.current) return;
    const fields = lastSyncedFieldsRef.current;
    if (Object.keys(fields).length > 0) {
      logger.info('Re-applying synced preferences after map data init');
      applyRemoteFields(fields);
    }
    setNeedsBaselineSeed(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapData, syncCount, applyRemoteFields]);

  // Seed baseline after re-apply dispatches have propagated through a React render,
  // so change detection has accurate starting values and works immediately.
  useEffect(() => {
    if (!needsBaselineSeed) return;
    setNeedsBaselineSeed(false);
    const currentState = collectReduxState(selectedMarkerId, activeCategoryId, sidebarOpen, filters, layerVisibility, showTeleportWarning, trackedCompassPin, activeDevelopmentMessageDismissedAt);
    for (const key of fieldKeys) {
      lastSentValuesRef.current[key] = fieldConfig[key].serialize(currentState);
    }
    baselineSeededRef.current = true;
    logger.info('Baseline seeded — change detection active');
    setPrefsSettled(true);
  }, [needsBaselineSeed, selectedMarkerId, activeCategoryId, sidebarOpen, filters, layerVisibility, showTeleportWarning, trackedCompassPin, activeDevelopmentMessageDismissedAt, fieldConfig, fieldKeys]);
  useEffect(() => {
    if (!baselineSeededRef.current || isRemoteUpdateRef.current) return;
    const currentState = collectReduxState(selectedMarkerId, activeCategoryId, sidebarOpen, filters, layerVisibility, showTeleportWarning, trackedCompassPin, activeDevelopmentMessageDismissedAt);
    let hasChanges = false;
    for (const key of fieldKeys) {
      const serialized = fieldConfig[key].serialize(currentState);
      const lastSent = lastSentValuesRef.current[key];
      if (lastSent !== undefined && serialized !== lastSent) {
        const now = Date.now();
        pendingChangesRef.current[key] = {
          value: serialized,
          updatedAt: now
        };
        localTimestampsRef.current[key] = now;
        lastSentValuesRef.current[key] = serialized;
        hasChanges = true;
      }
    }
    if (!hasChanges) return;
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      const fields = pendingChangesRef.current;
      pendingChangesRef.current = {};
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        logger.info(`Sending update: ${Object.keys(fields).join(', ')}`);
        sendMessage({
          type: 'preferences_update',
          fields
        });
      } else {
        pendingChangesRef.current = {
          ...fields,
          ...pendingChangesRef.current
        };
      }
    }, DEBOUNCE_MS);
  }, [selectedMarkerId, activeCategoryId, sidebarOpen, filters, layerVisibility, showTeleportWarning, trackedCompassPin, activeDevelopmentMessageDismissedAt, fieldConfig, fieldKeys, sendMessage]);
  if (!isAuthenticated) {
    const isAwaitingHostAuthToken = canUseHostAuth && !mapExternalId;
    return {
      isReady: !isAwaitingHostAuthToken,
      isConnected: false,
      hasStoredPreferences: false,
      onRemoteMarkerSelectRef,
      sendMessageRef,
      requestMiniMapOpen
    };
  }

  // Only report ready once preferences are fully settled:
  // sync received AND re-applied after map data init (no flash of defaults)
  const isReady = syncReceived && prefsSettled;
  return {
    isReady,
    isConnected,
    hasStoredPreferences,
    onRemoteMarkerSelectRef,
    sendMessageRef,
    requestMiniMapOpen
  };
}